import React, { createContext, useCallback, useContext, useState } from 'react';
import { OnboardingController } from 'api';
import type { IOnboardingGetProfileResponse } from '@/app-package';
import { message } from '@bb/elements-web';

export enum SingleAccessFormStatus {
  DEFAULT = 'DEFAULT',
  SUBMITTING = 'SUBMITTING',
  SUCCESS = 'SUCCESS',
  NOT_FOUND = 'NOT_FOUND',
}

interface SingleAccessSetupContextValue {
  /** The user claim hash */
  claimHash: string;
  /** Setter function for the user claim hash */
  setClaimHash: React.Dispatch<React.SetStateAction<string>>;
  /** Function to submit the user's profile */
  submitProfile: (email: string) => Promise<void>;
  /** Function to fetch the user's profile */
  fetchProfile: () => Promise<void>;
  /** The current status of the form */
  status: SingleAccessFormStatus;
  /** Setter function for the form status */
  setStatus: React.Dispatch<React.SetStateAction<SingleAccessFormStatus>>;
  /** The fetched user data */
  userData: IOnboardingGetProfileResponse | null;
}

export const SingleAccessSetupContext = createContext<
  undefined | SingleAccessSetupContextValue
>(undefined);

export const useSingleAccessSetupContext = () => {
  const singleAccessSetupContext = useContext(SingleAccessSetupContext);

  if (!singleAccessSetupContext) {
    return null;
  }

  return singleAccessSetupContext;
};

export const SingleAccessSetupContextProvider: React.FC = props => {
  const { children } = props;

  const [status, setStatus] = useState<SingleAccessFormStatus>(
    SingleAccessFormStatus.DEFAULT,
  );
  const [claimHash, setClaimHash] = useState<string>();
  const [userData, setUserData] =
    useState<IOnboardingGetProfileResponse | null>(null);

  /** Submit the single access email update */
  const submitProfile = async (email: string) => {
    if (!userData) return;

    setStatus(SingleAccessFormStatus.SUBMITTING);

    try {
      await OnboardingController.updateSingleAccessProfile(
        { hash: claimHash },
        {
          email,
        },
      );

      setStatus(SingleAccessFormStatus.SUCCESS);
    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message, 7);
      } else {
        message.error('Something went wrong. Please try again.', 7);
      }

      setStatus(SingleAccessFormStatus.DEFAULT);
    }
  };

  /** Fetch the user profile by the user claim hash */
  const fetchProfile = useCallback(async () => {
    if (!claimHash) return;

    try {
      const profile = await OnboardingController.getProfile({
        hash: claimHash,
      });

      if (!profile) {
        setStatus(SingleAccessFormStatus.NOT_FOUND);
        return;
      }

      setUserData(profile);
    } catch (err) {
      setStatus(SingleAccessFormStatus.NOT_FOUND);
    }
  }, [claimHash]);

  return (
    <SingleAccessSetupContext.Provider
      value={{
        claimHash,
        setClaimHash,
        submitProfile,
        fetchProfile,
        status,
        setStatus,
        userData,
      }}
    >
      {children}
    </SingleAccessSetupContext.Provider>
  );
};
